import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Responsive } from '@cian/ui-kit';
import { useNewbuildingCountLink, useNewbuildingLinks } from './hooks';
import { closePromoDetails } from '../../actions/promoDetails';
import { PromoDetailsModal } from '../../components/PromoDetailsModal';
import { PROMO_ID_QUERYSTRING } from '../../constants/promoIdQuery';
import { selectCurrentPromoId, selectPromoDetails } from '../../selectors/promoDetails';
import { IPromoOffer } from '../../types/promoOffersSelection';
import { PromoOfferCardContext } from '../../utils/promoOfferCardContext';
import { isPathContainsPromoId } from '../../utils/topPromoUrl/isPathContainsPromoId';
import { getLocationPath } from '../../utils/url';
import { removeSearchParam } from '../../utils/url/removeSearchParam';
import { CallButton } from '../CallButton';
import { MobileCallButton } from '../CallButton/MobileCallButton';
import { trackCallButtonClick } from '../CallButton/tracking';
import { NewbuildingLinksListContainer } from '../NewbuildingLinksListContainer';
import { OffersLinkContainer } from '../OffersLinkContainer';

export const PromoDetailsModalContainer = () => {
  const promoDetails = useSelector(selectPromoDetails);
  const promoId = useSelector(selectCurrentPromoId);
  const dispatch = useDispatch();

  const newbuildingCountLink = useNewbuildingCountLink(promoDetails);
  const newbuildingLinks = useNewbuildingLinks(promoDetails);

  const closePromoDetailsModal = React.useCallback(() => {
    const path = getLocationPath();
    const hasPromoId = isPathContainsPromoId({ path });

    if (hasPromoId) {
      const cleanPath = removeSearchParam({ path, param: PROMO_ID_QUERYSTRING });
      window.history.pushState({}, '', cleanPath);
    }

    dispatch(closePromoDetails());
  }, [dispatch]);

  const onCallClick = React.useCallback(() => {
    const id = promoDetails?.phoneNewbuildingId;
    const gaLabel = promoDetails?.gaLabel;

    if (!id || !gaLabel) {
      return;
    }

    trackCallButtonClick({
      customUrl: gaLabel,
      id,
      action: 'Open_detail_promo',
      extra: { promoId },
    });
  }, [promoDetails, promoId]);

  const callButtonContext = React.useMemo(() => {
    const phone = promoDetails?.builderPhone;
    if (phone && phone.countryCode) {
      phone.countryCode = phone.countryCode.replace(/\D/g, '');
    }

    const builder = {
      phones: promoDetails?.builderPhone ? [promoDetails.builderPhone] : [],
    };

    return {
      builder,
      siteBlockId: promoDetails?.siteBlockId,
      offerId: promoDetails?.phoneNewbuildingId,
    } as IPromoOffer;
  }, [promoDetails]);

  if (!promoId || !promoDetails) {
    return null;
  }

  return (
    <PromoDetailsModal
      onClose={closePromoDetailsModal}
      banks={promoDetails.banks}
      promoName={promoDetails.promoName}
      providerName={promoDetails.providerName}
      logo={promoDetails.providerLogo}
      description={promoDetails.promoDescription}
      features={promoDetails.promoProps}
      newbuildingLinksList={
        <NewbuildingLinksListContainer
          newbuildingCountText={promoDetails.newbuildingCountText}
          newbuildingCountLink={newbuildingCountLink}
          newbuildingLinks={newbuildingLinks}
          promoId={promoId}
        />
      }
      linkButton={
        <>
          <OffersLinkContainer
            promoId={promoId}
            promoType={promoDetails.promoType}
            newbuildingIds={promoDetails.newbuildingIds}
          />
          <PromoOfferCardContext.Provider value={callButtonContext}>
            <Responsive desktop tablet>
              <CallButton onClick={onCallClick} theme="fill_primary" size="M" placeType="promoPopup" />
            </Responsive>
            <Responsive phone>
              <MobileCallButton placeType="promoPopup" />
            </Responsive>
          </PromoOfferCardContext.Provider>
        </>
      }
    />
  );
};
